import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Slate, Editable, withReact } from "slate-react";
import { createEditor } from "slate";
import { withHistory } from "slate-history";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import styled, { keyframes } from "styled-components";
import { shortTimecode } from "../TimeCodeConverter";
import { isArray } from "lodash";
import { Tooltip } from "@material-ui/core";

const Index = ({
  data,
  timecodeOffset,
  showTimecodes,
  currentTime,
  showSpeakers,
  handleWordClick,
  render,
  setRender,
  searchText = "",
  searchMatches = [],
  currentMatchIndex = 0,
  currentMatch = null,
  isParagraphVisible = () => true,
  isFiltering = false,
}) => {
  const editor = useMemo(() => withReact(withHistory(createEditor())), []);
  const [value, setValue] = useState([]);
  const containerRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      }, 20);
    }
  }, []);

  useEffect(() => {
    const response = splitContent(data, currentTime);
    setValue(response);
    setRender(false);
    if (value) {
      editor.children = value;
      requestAnimationFrame(scrollToBottom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [render]);

  function generatePreviousTimingsUpToCurrent(start, end) {
    const startTimeInt = parseInt(start);
    const endTimeInt = parseInt(end);
    return `${startTimeInt} ${endTimeInt}`;
  }

  // Helper function to check if text range is a search match
  const getSearchHighlightColor = (paragraphIndex, startPos, endPos) => {
    if (!searchText || !searchMatches?.length) return null;

    for (let i = 0; i < searchMatches.length; i++) {
      const match = searchMatches[i];
      if (
        match.paragraphIndex === paragraphIndex &&
        match.startPos <= startPos &&
        endPos <= match.endPos
      ) {
        // Check if this is the current match
        return i === currentMatchIndex ? "#FFC107" : "#FFEB99";
      }
    }
    return null;
  };

  const renderElement = useCallback((props) => {
    
    switch (props.element.type) {
      case "timedText":
        // Check if this paragraph should be visible based on search filter
        if (isFiltering && !isParagraphVisible(props.element.paragraphIndex)) {
          return null; // Hide non-matching paragraphs
        }
        return <TimedTextElement {...props} />;
        default:
          return <DefaultElement {...{ props }} />;
        }
      }, [isFiltering, isParagraphVisible]);
      
      const DefaultElement = (props) => {
        return <p {...props.attributes}>{props.children}</p>;
      };
      
      const TimedTextElement = (props) => {
        let startTimecode = props.element?.start;
        if (timecodeOffset) {
          startTimecode += timecodeOffset;
        }

    return (
      <TranscriptBlock>
        <MetadataBar>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {showSpeakers && (
              <SpeakerBadge>{props.element.speaker}</SpeakerBadge>
            )}
            {showTimecodes && (
              <TimecodeChip
                data-start={props.element.start}
                onClick={() => handleWordClick(startTimecode)}
              >
                {shortTimecode(startTimecode)}
              </TimecodeChip>
            )}
          </div>
          <div
            style={{
              fontSize: "11px",
              fontFamily: theme.fonts.primaryFontSemiBold,
              color: "#64748b",
            }}
          >
            {props.element?.id}
          </div>
        </MetadataBar>

        <TextContent onDoubleClick={() => handleWordClick(startTimecode)}>
          {props.children}
        </TextContent>
      </TranscriptBlock>
    );
  };

  const generateText = (words) => {
    return words.text;
  };

  const splitContent = (paragraphs) => {
    return paragraphs.map((paragraph, index) => ({
      speaker: paragraph.speaker,
      start: paragraph.start,
      confidence: paragraph.confidence,
      id: paragraph.id,
      end: paragraph.end,
      paragraphIndex: index,
      previousTimings: generatePreviousTimingsUpToCurrent(
        paragraph.start,
        paragraph.end,
      ),
      mediaId: paragraph?.mediaId,
      // pre-computing the display of the formatting here so that it doesn't need to convert it in leaf render
      startTimecode: paragraph.start,
      type: "timedText",
      children: isArray(paragraph.text)
        ? paragraph.text
        : [
            {
              text: generateText(paragraph),
            },
          ],
    }));
  };

  const renderLeaf = useCallback(({ attributes, children, leaf }) => {
    const paragraphIndex = children.props?.parent?.paragraphIndex;
    let backgroundColor = leaf["color"];

    // Check if this text is part of a search match
    if (searchText && searchMatches?.length > 0 && paragraphIndex !== undefined) {
      const textContent = leaf.text || "";
      
      // Check if any search match overlaps with this text in the paragraph
      for (let i = 0; i < searchMatches.length; i++) {
        const match = searchMatches[i];
        if (match.paragraphIndex === paragraphIndex) {
          // Check if match overlaps with the text position
          // This is a simple check - highlight if the match is in this paragraph
          // In a real implementation, you'd track character positions
          const highlightColor = i === currentMatchIndex ? "#FFC107" : "#FFEB99";
          
          // For now, highlight all text in paragraphs that contain matches
          // A more precise implementation would track exact positions
          if (textContent.toLowerCase().includes(searchText.toLowerCase())) {
            backgroundColor = highlightColor;
          }
        }
      }
    }

    if (leaf["suggestionText"] || leaf["name"]) {
      return (
        <Tooltip
          title={
            leaf["name"]
              ? `${
                  leaf["suggestionText"] ? leaf["suggestionText"] : leaf["role"]
                }  - ${leaf["name"]}`
              : leaf["suggestionText"]
          }
          placement="top"
        >
          <HighlightedText
            className={"timecode text"}
            style={{
              borderRadius: "5px",
              backgroundColor: backgroundColor,
            }}
            data-start={children.props.parent.start}
            data-previous-timings={children.props.parent.previousTimings}
            {...attributes}
          >
            {children}
          </HighlightedText>
        </Tooltip>
      );
    }
    return (
      <HighlightedText
        className={"timecode text"}
        style={{
          borderRadius: "5px",
          backgroundColor: backgroundColor,
        }}
        data-start={children.props?.parent?.start}
        data-previous-timings={children.props?.parent?.previousTimings}
        {...attributes}
      >
        {children}
      </HighlightedText>
    );
  }, [searchText, searchMatches, currentMatchIndex]);

  const handleTimeClick = () => {
    editor.selection = null;
  };

  const highlightText = (currentTime) => {
    let boolean;
    if (currentTime) {
      for (let i = 0; i < value.length; i++) {
        let startTime = parseInt(value[i].start);
        let endTime = parseInt(value[i].end);
        if (startTime <= currentTime && endTime >= currentTime) {
          boolean = `${startTime} ${endTime}`;
          break;
        }
      }
      return boolean;
    }
  };

  return (
    <>
      <TranscriptWrapper>
        <LiveStatusBar>
          <StatusIndicator $connected={true}></StatusIndicator>
          <SegmentCount>
            {editor.children.length}{" "}
            {editor.children.length === 1 ? "segment" : "segments"}
          </SegmentCount>
        </LiveStatusBar>
        <style scoped>
          {`
             .timecode[data-previous-timings*="${highlightText(currentTime)}"]{
                  color:  ${COLORS.BTN_GREEN};
              }
          `}
        </style>
        {value.length !== 0 ? (
          <TranscriptContent ref={containerRef}>
            <Slate editor={editor} value={value}>
              <Editable
                readOnly={true}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                onMouseDown={handleTimeClick}
              />
            </Slate>
          </TranscriptContent>
        ) : null}
      </TranscriptWrapper>
    </>
  );
};

export default Index;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0);   }
`;

export const QuitContainer = styled.div`
  padding: 18px;
  margin-left: 117px;
  margin-right: 25px;
  border-radius: 7px;
  background-color: ${COLORS.LIGHT_BLUE};
  border: solid 1px ${COLORS.COLOR_DARK};
  margin-bottom: 12px;
  max-width: 80%;
`;

export const ProfileImage = styled.img`
  width: 20px;
  height: 25px;
  border-radius: 50%;
  object-fit: contain;
  margin-right: 4px;
  margin-left: 5px;
`;

const TranscriptBlock = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 12px;
  transition: all 0.18s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }
`;

const MetadataBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 10px;
`;

const SpeakerBadge = styled.div`
  display: inline-flex;
  align-items: center;
  // background: ${(p) => (p.$interim ? "#fbbf24" : "#91C4C3")};
  color: #00838c;
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
`;

const TimecodeChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  background: ${(p) => (p.$interim ? "#fef3c7" : "#ecfdf5")};
  color: ${(p) => (p.$interim ? "#d97706" : "#059669")};
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;

  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;

const TextContent = styled.div`
  color: ${COLORS.COLOR_DARK};
  font-size: 15.5px;
  line-height: 1.68;
  cursor: text;
  font-family: ${theme.fonts.primaryFontSemiBold};
  margin-left: 10px;
`;

const HighlightedText = styled.span`
  transition: background 0.15s;
  &:hover {
    background: rgba(16, 185, 129, 0.12);
  }
`;

const TranscriptContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 7px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
`;

const TranscriptWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    sans-serif;
`;

const LiveStatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
  color: #475569;
`;

const SegmentCount = styled.div`
  background: #f1f5f9;
  padding: 4px 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  border-radius: 6px;
  font-size: 12px;
  color: #64748b;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${(p) => (p.$connected ? "#059669" : "#d97706")};
`;
